/**
 * KAN-713: выбор "Прокси из справочника" над полями прокси в формах аккаунта.
 * Выбранный прокси уходит в запрос как proxy_id, поля прокси скрываются
 * (manual - поля ввода, показываются, пока прокси не выбран).
 * Пустой справочник: выбор не показывается, остаются только поля.
 */
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useTelegramProxies } from '@/hooks/useTelegramProxies';
import {
  DIRECTORY_SELECT_LABEL,
  MANUAL_ENTRY_LABEL,
  proxyOptionLabel,
} from '@/lib/telegramProxyDirectory';

interface Props {
  idPrefix: string;
  /** id прокси из справочника строкой; "" - ввод вручную. */
  proxyId: string;
  onChange: (proxyId: string) => void;
  disabled?: boolean;
  /** Поля ручного ввода. */
  manual: ReactNode;
}

export function ProxyDirectoryPicker({
  idPrefix,
  proxyId,
  onChange,
  disabled,
  manual,
}: Props): JSX.Element {
  const { data } = useTelegramProxies();
  const proxies = data ?? [];
  if (!proxies.length && !proxyId) return <>{manual}</>;

  const id = `${idPrefix}-directory`;
  const options = [
    { value: '', label: MANUAL_ENTRY_LABEL },
    ...proxies.map((p) => ({ value: String(p.id), label: proxyOptionLabel(p) })),
  ];

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label htmlFor={id}>{DIRECTORY_SELECT_LABEL}</Label>
        <NativeSelect
          id={id}
          data-testid={id}
          aria-label={DIRECTORY_SELECT_LABEL}
          options={options}
          value={proxyId}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {proxyId ? (
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs"
          data-testid={`${id}-manual`}
          disabled={disabled}
          onClick={() => onChange('')}
        >
          {MANUAL_ENTRY_LABEL}
        </Button>
      ) : (
        manual
      )}
    </div>
  );
}
